import { useCallback, useState, useEffect } from 'react';
import { SerializedError } from '@reduxjs/toolkit';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { locations } from 'services/routes/locations';
import distributionApi from 'services/api/distribution';
import ArtistName from '../ArtistName';
import WarningModal from '../WarningModal';
import { Container, ConfirmButton, StyledStack } from './styled';

type PropsType = {
  releaseId: string;
};

const Info = ({ releaseId }: PropsType) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [
    setupArtistName,
    {
      isSuccess: confirmationSucceeded,
      isLoading: confirmationLoading,
      error: confirmationError,
    },
  ] = distributionApi.useSetupArtistNameMutation();

  const [artistName, setArtistName] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<SerializedError | null>(null);

  const [isWarningModalOpen, setIsWarningModalOpen] = useState(false);

  useEffect(() => {
    setLoading(confirmationLoading);
  }, [confirmationLoading]);

  useEffect(() => {
    setError(confirmationError as SerializedError);
  }, [confirmationError]);

  useEffect(() => {
    if (!confirmationSucceeded) {
      return;
    }
    navigate(locations.distribution.tracklist.pathname(releaseId));
  }, [confirmationSucceeded, releaseId, navigate]);

  const handleValidationStarted = useCallback((name: string | null) => {
    setLoading(true);
    setError(null);
    setArtistName(name);
  }, []);

  const handleValidationEnded = useCallback((err: SerializedError | null) => {
    setLoading(false);
    setError(err);
  }, []);

  const handleArtistNameConfirmation = useCallback(() => {
    setIsWarningModalOpen(false);
    if (!artistName) {
      return;
    }
    setupArtistName({ name: artistName, save: true });
  }, [artistName, setupArtistName]);

  return (
    <Container>
      <StyledStack>
        <ArtistName
          error={error}
          loading={loading}
          onValidationStarted={handleValidationStarted}
          onValidationEnded={handleValidationEnded}
        />
        <ConfirmButton
          disabled={!artistName || !!error || loading}
          variant="contained"
          loading={confirmationLoading}
          onClick={() => setIsWarningModalOpen(true)}
        >
          {t('distribution:artistName:confirm')}
        </ConfirmButton>
      </StyledStack>
      <WarningModal
        isOpen={isWarningModalOpen}
        onSubmit={handleArtistNameConfirmation}
        onClose={() => setIsWarningModalOpen(false)}
      />
    </Container>
  );
};

export default Info;
